/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import styled from '@emotion/styled';

import { Checkbox, Grid, Link, Typography, Typography2 } from '@coursera/cds-core';

import { useEnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import InformationCardToggle from 'bundles/enterprise-legacy-learner-home/components/InformationCard/InformationCardToggle';
import {
  getSkillSetAcademies,
  isInBeta,
  matchDataTechAcademies,
} from 'bundles/enterprise-skillset-bank/utils/academies';
import { BetaBadge, Button, Labeled, Spacer } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/enterprise-admin-skills';

const AcademySkills = styled.div({
  background: 'var(--cds-color-neutral-background-primary-weak)',
  padding: '36px 32px',
});

const AcademyBox = styled.div({
  padding: '12px 12px 0 12px',
  display: 'flex',
  flexDirection: 'column',
});

const Selected = styled.div({
  backgroundColor: 'var(--cds-color-blue-50)',
  borderRadius: '0px 0px 3px 3px',
  textTransform: 'uppercase',
  height: '31px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const academyVisualStyle = {
  margin: '0 auto',
};

const Academy: React.FC<{
  id: string;
  name: string;
  visual: React.ComponentType<React.SVGAttributes<SVGElement>>;
  readOnly: boolean;
  selected: boolean;
  onToggle: () => void;
}> = ({ id, name, visual: Visual, readOnly, selected, onToggle }) => {
  const { enableDataTechSku } = useEnterpriseAdminContext();
  return enableDataTechSku && !matchDataTechAcademies([id]) ? null : (
    <Grid item>
      <Labeled>
        <div
          css={{
            background: 'var(--cds-color-neutral-background-primary)',
            border:
              selected && !readOnly ? '1px solid var(--cds-color-blue-600)' : '1px solid var(--cds-color-grey-200)',
            borderRadius: '3px',
            width: '200px',
            minHeight: '176px',
            margin: '3px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <AcademyBox>
            {!readOnly && <Checkbox checked={selected} onChange={onToggle} data-e2e={id} label={name} />}
            {readOnly && <Spacer size={12} />}
            <Visual style={academyVisualStyle} />
            <Spacer size={24} />
            <div css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {readOnly && <Typography2 component="p">{name}</Typography2>}
              {readOnly && isInBeta(id) && (
                <BetaBadge css={{ marginLeft: 'var(--cds-spacing-50)', textTransform: 'uppercase' }} />
              )}
              {!readOnly && isInBeta(id) && <BetaBadge css={{ marginTop: -16, textTransform: 'uppercase' }} />}
            </div>
          </AcademyBox>
          {readOnly && selected && (
            <Selected>
              <Typography2 component="strong">{_t('Selected')}</Typography2>
            </Selected>
          )}
        </div>
      </Labeled>
    </Grid>
  );
};

const AcademySelection = ({
  readOnly,
  selectedAcademyIds,
  onChange,
  onChangeSelections,
}: {
  readOnly: boolean;
  selectedAcademyIds: string[];
  onChange: (ids: string[]) => void;
  onChangeSelections: () => void;
}) => {
  const [isInformationCardOpen, setIsInformationCardOpen] = React.useState(false);
  const { enableDataTechSku } = useEnterpriseAdminContext();
  const allAcademies = getSkillSetAcademies(enableDataTechSku);
  const allAcademyIdsSorted = allAcademies.map((_) => _.id).sort();

  const toggleInformationCard = () => {
    setIsInformationCardOpen((_) => !_);
  };

  const getAreAllAcademiesSelected = () => {
    return allAcademyIdsSorted.join(',') === selectedAcademyIds.join(',');
  };

  const toggleAllAcademies = () => {
    const areAllAcademiesSelected = getAreAllAcademiesSelected();
    onChange(areAllAcademiesSelected ? [] : allAcademyIdsSorted);
  };

  const areAllAcademiesSelected = getAreAllAcademiesSelected();

  return (
    <AcademySkills>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold">{_t('Select a skills Academy')}</Typography>
      <Spacer size={8} />
      <Typography2 component="p">
        {_t('The following academies are specific to the contracts of this organization. ')}
      </Typography2>
      <Spacer size={12} />
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <div data-e2e="what-are-academies">
          <InformationCardToggle
            label={_t('What are Academies from Coursera?')}
            isOpen={isInformationCardOpen}
            onClick={toggleInformationCard}
            trackingName="about_target_levels_information_link"
            cardHtmlId="admin-dashboard-organization-skillset-target-information"
          />
        </div>
        {!readOnly && !isInformationCardOpen && (
          <Checkbox
            checked={areAllAcademiesSelected}
            onChange={toggleAllAcademies}
            data-e2e="toggle-all-academies"
            label={areAllAcademiesSelected ? _t('Deselect all skills academies') : _t('Select all skills academies')}
          />
        )}
      </div>
      {isInformationCardOpen && (
        <div data-e2e="academy-definition">
          <Spacer size={12} />
          <Typography2 component="p">
            {_t(
              "Coursera's academies are designed based on insights gained from over 2,700 customers. Each academy includes expert-selected SkillSets that assist companies in upskilling for digital transformation. These SkillSets aim to enhance tech and data literacy, train specific teams with particular tools, and reskill employees for in-demand roles."
            )}{' '}
            <Link
              typographyVariant="bodyPrimary"
              href="https://business.coursera.help/hc/articles/360055768313"
              target="_blank"
              rel="noopener noreferrer"
            >
              {_t('Learn more about Coursera Academies')}
            </Link>
          </Typography2>
          <Spacer size={12} />
        </div>
      )}
      {!readOnly && isInformationCardOpen && (
        <Checkbox
          checked={areAllAcademiesSelected}
          onChange={toggleAllAcademies}
          data-e2e="toggle-all-academies"
          label={areAllAcademiesSelected ? _t('Deselect all skills academies') : _t('Select all skills academies')}
        />
      )}
      <Spacer size={24} />
      <Grid container spacing={16}>
        {allAcademies.map((academy) => (
          <Academy
            key={academy.id}
            {...academy}
            readOnly={readOnly}
            selected={selectedAcademyIds.includes(academy.id)}
            onToggle={() =>
              onChange(
                selectedAcademyIds.includes(academy.id)
                  ? selectedAcademyIds.filter((_) => _ !== academy.id)
                  : [...selectedAcademyIds, academy.id].sort()
              )
            }
          />
        ))}
      </Grid>
      {readOnly && (
        <div>
          <Spacer size={24} />
          <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onChangeSelections}>
              {_t('Change selections')}
            </Button>
          </div>
        </div>
      )}
    </AcademySkills>
  );
};

export default AcademySelection;
